import { Box, Button, Link } from '@mui/material';
import { createSelector } from '@reduxjs/toolkit';
import { useFormik } from 'formik';
import { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';

import { offerSelected } from '../../../../app/reducers/offer-selected';
import { Offer } from '../../../../domain/qualification';
import { RequiredText } from '../../../../general/components/required-text';
import { FormikToggle } from '../../../../general/inputs/formik-toggle';
import { recordOfferConsent, selectConsent } from '../../../legal-consent/reducers/consent';
import { selectOffer } from '../../reducers/credit-terms.reducer';
import { nextStep, selectnextStep } from '../../reducers/next-step';

const savingSelector = createSelector(
  [selectConsent, selectnextStep],
  (consent, { loading }) => consent.loading || loading,
);

const validationSchema = yup.object({
  consentOfferSummary: yup.mixed().oneOf([true], 'You must read and accept the Coast Offer Summary'),
});

type Props = {
  offer: Offer & { offerSummaryUrl: string };
};

const SingleOfferConsentForm: FC<Props> = ({ offer }) => {
  const dispatch = useDispatch();
  const saving = useSelector(savingSelector);

  const formik = useFormik({
    initialValues: { consentOfferSummary: false },
    validationSchema,
    validateOnChange: false,
    onSubmit: () => {
      dispatch(
        recordOfferConsent({
          offerId: offer.id,
          onSuccess: [
            offerSelected({ offer: offer.offerIndex }),
            selectOffer(offer.id),
            nextStep({ action: 'select_offer', gotoNextPage: true }),
          ],
        }),
      );
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Box mt={3}>
        <FormikToggle
          formik={formik}
          id="consentOfferSummary"
          name="consentOfferSummary"
          label={
            <RequiredText>
              I have reviewed and acknowledge receipt of the{' '}
              <Link href={offer.offerSummaryUrl} target="_blank">
                Coast Offer Summary
              </Link>
            </RequiredText>
          }
          toggleStyle="checkbox"
        />
      </Box>
      <Box mt={3}>
        <Button color="primary" variant="contained" size="large" fullWidth type="submit" disabled={saving}>
          Next
        </Button>
      </Box>
    </form>
  );
};

export { SingleOfferConsentForm };
